/**
 * ServiceOrderServicesCompleteDialog - "Serviços executados": Sim/Não por serviço do negócio,
 * observação opcional e motivo obrigatório no Não (BR-OS-047).
 *
 * Componente sem sessão: usado no desktop e no portal do prestador, que passam
 * os itens e a função de gravação.
 *
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  initialServiceAnswers, toServiceResultsPayload, validateServiceAnswers,
  type ServiceAnswer, type ServiceOrderServiceItem,
} from '@/lib/serviceOrders/serviceItems';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Somente os itens que exigem resposta (servicesRequiringAnswer) */
  items: ServiceOrderServiceItem[];
  saving?: boolean;
  /** Texto do botão principal (ex.: "Salvar e concluir") */
  confirmLabel?: string;
  onConfirm: (payload: ReturnType<typeof toServiceResultsPayload>) => Promise<void> | void;
}

export function ServiceOrderServicesCompleteDialog({ open, onOpenChange, items, saving, confirmLabel = 'Salvar', onConfirm }: Props) {
  const [answers, setAnswers] = useState<ServiceAnswer[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Reabre com o que já foi gravado
  useEffect(() => { if (open) { setAnswers(initialServiceAnswers(items)); setError(null); } }, [open, items]);

  const patch = (id: string, p: Partial<ServiceAnswer>) => setAnswers((list) => list.map((a) => (a.id === id ? { ...a, ...p } : a)));

  const submit = async () => {
    const err = validateServiceAnswers(answers);
    if (err) return setError(err);
    await onConfirm(toServiceResultsPayload(answers));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="border-b p-4">
          <DialogTitle>Serviços executados</DialogTitle>
          <DialogDescription>Informe se cada serviço do negócio foi realizado. No "Não", o motivo é obrigatório.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {items.map((item) => {
            const a = answers.find((x) => x.id === item.id);
            if (!a) return null;
            return (
              <div key={item.id} className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="min-w-0 truncate text-sm font-medium">{item.product_name} <span className="text-muted-foreground">· {Number(item.quantity)}x</span></p>
                  <div className="flex shrink-0 gap-1">
                    <Button type="button" size="sm" variant="outline" onClick={() => patch(item.id, { done: true })}
                      className={cn(a.done === true && 'border-success bg-success/10 text-success')}><Check className="mr-1 h-3.5 w-3.5" />Sim</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => patch(item.id, { done: false })}
                      className={cn(a.done === false && 'border-destructive bg-destructive/10 text-destructive')}><X className="mr-1 h-3.5 w-3.5" />Não</Button>
                  </div>
                </div>
                {a.done === false && (
                  <Textarea rows={2} maxLength={500} value={a.reason} placeholder="Motivo de não ter realizado *"
                    onChange={(e) => patch(item.id, { reason: e.target.value })} />
                )}
                <Textarea rows={2} maxLength={500} value={a.notes} placeholder="Observação (opcional)"
                  onChange={(e) => patch(item.id, { notes: e.target.value })} />
              </div>
            );
          })}
        </div>
        <DialogFooter className="border-t p-4">
          {error && <p className="mr-auto self-center text-xs text-destructive">{error}</p>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Voltar</Button>
          <Button onClick={submit} disabled={saving}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
